import React from 'react';
import _ from 'underscore';

import {SequenceSchemaType} from 'constants/RegistryConstants';
import StaticSchemaInterfaceFieldMapping, {
  StaticInterfaceField,
  StaticSchemaInterfaceFieldDefinition,
} from 'schemas/static-schema-interface/StaticSchemaInterfaceFieldMapping';
import {SettingsLabel} from 'ui-library/SettingsField';

import {TagSchemaSummaryGraphQL} from '../../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {EXTENDED_CONJUGATE_FOLDER_ITEM_TYPES} from '../ComplexPolymerSchemaConfiguration';
import {DraftImplementedOligoConjugateInterface} from '../ComplexPolymerSchemaInterfaceConstants';

export const OLIGO_FIELD_DEFINITION: StaticSchemaInterfaceFieldDefinition = {
  key: 'oligoSchemaFieldSpec',
  displayName: 'Backbone',
  placeholder: 'Select oligo schema',
  canMapToMultipleValues: false,
  isRequired: true,
  schemaCriteria: {
    allowedFolderItemTypes: [SequenceSchemaType.OLIGO, SequenceSchemaType.RNA_OLIGO],
  },
};

export function getConjugatesFieldDefinition(): StaticSchemaInterfaceFieldDefinition {
  return {
    key: 'conjugatesSchemaFieldSpecs',
    displayName: 'Components',
    placeholder: 'Select component schemas',
    canMapToMultipleValues: true,
    isRequired: true,
    schemaCriteria: {
      allowedFolderItemTypes: EXTENDED_CONJUGATE_FOLDER_ITEM_TYPES,
    },
  };
}

function resolveConjugateSpecsConflicts(
  implementedInterface: DraftImplementedOligoConjugateInterface
): DraftImplementedOligoConjugateInterface {
  const oligoSchemaFieldName = implementedInterface.oligoSchemaFieldSpec?.schemaFieldName;
  const conjugateSpecs = implementedInterface?.conjugatesSchemaFieldSpecs;
  const updatedConjugateSpecs = [];

  for (const conjugateSpec of conjugateSpecs ?? []) {
    if (conjugateSpec.schemaFieldName != null && oligoSchemaFieldName === conjugateSpec.schemaFieldName) {
      updatedConjugateSpecs.push({
        ...conjugateSpec,
        schemaFieldName: `${conjugateSpec.schemaFieldName} (Conjugate)`,
      });
    } else {
      updatedConjugateSpecs.push(conjugateSpec);
    }
  }

  return {
    ...implementedInterface,
    conjugatesSchemaFieldSpecs: updatedConjugateSpecs,
  };
}

export interface OligoConjugateConfigurationTableProps {
  oligoConjugateInterfaceImplementation: DraftImplementedOligoConjugateInterface;
  disabled: boolean;
  onChange: (config: DraftImplementedOligoConjugateInterface) => void;
  /* All tag schemas that currently exist on the registry */
  schemas: Array<TagSchemaSummaryGraphQL>;
}

export default function OligoConjugateConfigurationTable(
  props: OligoConjugateConfigurationTableProps
): JSX.Element {
  const schemasById = _.indexBy(props.schemas, 'id');
  const onChange = ({fieldDefinition, values}: StaticInterfaceField): void => {
    if (values == null) {
      props.onChange({
        ...props.oligoConjugateInterfaceImplementation,
        [fieldDefinition.key]: null,
      });
      return;
    }

    if (fieldDefinition.key === OLIGO_FIELD_DEFINITION.key) {
      const spec =
        values.length === 0
          ? null
          : {
              schemaFieldName: schemasById[values[0]].name,
              schemaOrInterfaceId: values[0],
            };
      props.onChange({
        ...props.oligoConjugateInterfaceImplementation,
        [fieldDefinition.key]: spec,
      });
    } else {
      const newImplementedInterface = {
        ...props.oligoConjugateInterfaceImplementation,
        [fieldDefinition.key]: values.map((value) => ({
          schemaFieldName: schemasById[value].name,
          schemaOrInterfaceId: value,
        })),
      };
      props.onChange(resolveConjugateSpecsConflicts(newImplementedInterface));
    }
  };

  const oligoSchemaId =
    props.oligoConjugateInterfaceImplementation.oligoSchemaFieldSpec?.schemaOrInterfaceId ?? null;
  const conjugatesSchemaIds =
    props.oligoConjugateInterfaceImplementation.conjugatesSchemaFieldSpecs?.map(
      (spec) => spec.schemaOrInterfaceId
    ) ?? null;

  return (
    <StaticSchemaInterfaceFieldMapping
      disabled={props.disabled}
      onChange={onChange}
      schemas={props.schemas}
      staticFields={[
        {fieldDefinition: OLIGO_FIELD_DEFINITION, values: oligoSchemaId != null ? [oligoSchemaId] : null},
        {
          fieldDefinition: getConjugatesFieldDefinition(),
          values: conjugatesSchemaIds,
        },
      ]}
      implementedStaticSchemaInterface={props.oligoConjugateInterfaceImplementation}
      subtitle={
        <p>
          Please select the allowed schemas for the <b>backbone</b> and the attached <b>components</b>.
        </p>
      }
      title={<SettingsLabel label="Structural configuration" />}
    />
  );
}
